import Graphic from '@arcgis/core/Graphic';
import Geometry from '@arcgis/core/geometry/Geometry';
import {
  CIMSymbol,
  SimpleMarkerSymbol,
  SimpleLineSymbol,
  SimpleFillSymbol,
  TextSymbol,
} from '@arcgis/core/symbols';
import { arcgisToGeoJSON, geojsonToArcGIS } from '@terraformer/arcgis';
import { webMercatorToGeographic, xyToLngLat } from '@arcgis/core/geometry/support/webMercatorUtils';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import { TextUtils } from '@fulcrumapp/fulcrum-core';
import { CoordinateTypes, GeometricShape, LngLatCoordinate } from '../types/geo';
import {
  ArcgisJSON,
  graphicLineString,
  graphicPoint,
  graphicPolygon,
  symbolLineString,
  symbolPoint,
  symbolPolygon,
} from './graphic-elements';
import { DEFAULT_COLOR } from './colorizer';
import { GraphicStyle } from './graphic-styles';

export const LABEL_LAYER_ID = 'graphics-label-layer';
const MAX_LABEL_CHAR_COUNT = 45;
const LABEL_BOX_Y_OFFSET = 38;
const POINTER_SYMBOL_Y_OFFSET = 20;

export type SketchType = 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle';

const isLngLat = (candidate: any) => (
  Array.isArray(candidate)
    && candidate.length >= 2 // coordinates can have a height associated, which we ignore
    && typeof candidate[0] === 'number'
    && typeof candidate[1] === 'number'
);

const isScreenCoordinate = (coordinate: LngLatCoordinate) => {
  const [lng, lat] = coordinate;
  return lng < -180 || lng > 180 || lat < -90 || lat > 90;
};

const requiresConverting = (possibleCoordinate: CoordinateTypes) => {
  if (isLngLat(possibleCoordinate)) {
    const coordinate = possibleCoordinate as LngLatCoordinate;
    return isScreenCoordinate(coordinate);
  }

  return requiresConverting(possibleCoordinate[0] as LngLatCoordinate[]);
};

const normalizeGeometryToGeographic = (geometry: Geometry): Geometry => (
  geometry?.spatialReference?.isWebMercator
    ? webMercatorToGeographic(geometry)
    : geometry
);

const recursivelyConvertXyToLngLat = (possibleCoordinate: CoordinateTypes) => {
  if (isLngLat(possibleCoordinate)) {
    const coordinate = possibleCoordinate as LngLatCoordinate;
    return xyToLngLat(coordinate[0], coordinate[1]);
  }

  const mapped = possibleCoordinate.map((c) => recursivelyConvertXyToLngLat(c));
  return mapped;
};

export const convertToGeoJSON = (geometry: Geometry): GeometricShape => {
  const normalizedGeometry = normalizeGeometryToGeographic(geometry);
  // @ts-ignore
  const { longitude, latitude } = normalizedGeometry;
  if (longitude && latitude) {
    return { type: 'Point', coordinates: [longitude, latitude] };
  }

  const geoJson: GeometricShape = arcgisToGeoJSON(normalizedGeometry);

  const { type, coordinates } = geoJson;

  const mappedCoordinates = requiresConverting(coordinates)
    ? recursivelyConvertXyToLngLat(coordinates)
    : coordinates;

  return { type, coordinates: mappedCoordinates };
};

export const buildGraphic = (
  shape: GeometricShape,
  color?: string | null | undefined,
  style?: GraphicStyle | undefined,
): Graphic => {
  if (!shape) {
    throw new Error(`Invalid geometry: ${shape}`);
  }

  const { type } = shape;
  const statusColor = color ?? DEFAULT_COLOR;
  const arcgisJSON: ArcgisJSON = geojsonToArcGIS(shape);
  switch (type) {
    case 'Polygon':
    case 'MultiPolygon':
      return graphicPolygon(arcgisJSON, statusColor, style, shape);
    case 'LineString':
    case 'MultiLineString':
      return graphicLineString(arcgisJSON, statusColor, style, shape);
    case 'Point':
    case 'MultiPoint':
      return graphicPoint(arcgisJSON, statusColor, style, shape);
    default:
      throw new Error(`Invalid geometry type: ${type}`);
  }
};

export const buildSymbol = (
  type: string,
  color?: string | null | undefined,
  style?: GraphicStyle | undefined,
): SimpleMarkerSymbol | SimpleLineSymbol | SimpleFillSymbol | CIMSymbol => {
  const statusColor = color ?? DEFAULT_COLOR;

  switch (type) {
    case 'point':
      return symbolPoint(statusColor, style);
    case 'polyline':
      return symbolLineString(statusColor, style);
    case 'polygon':
      return symbolPolygon(statusColor, style);
    default:
      throw new Error(`Invalid symbol type: ${type}`);
  }
};

export const updateGraphicColor = (graphic: Graphic, color: string) => {
  const { geometry: { type } } = graphic;
  const symbol = buildSymbol(type, color);
  graphic.set('symbol', symbol);
};

export const updateGraphicSymbol = (graphic: Graphic, newSymbol: Symbol) => {
  graphic.set('symbol', newSymbol);
};

export const updateGraphicGeometry = (graphic: Graphic, newGeometry: Geometry) => {
  graphic.set('geometry', newGeometry);
};

export const findGraphicIdOnMapView = (mapView: MapView, itemId: string): Graphic | null => {
  for (const layer of mapView.map.layers.toArray()) {
    if (layer instanceof GraphicsLayer) {
      const foundGraphic = layer.graphics.find((graphic: Graphic) => graphic?.attributes?.shape?.item?._id === itemId);
      if (foundGraphic) {
        return foundGraphic;
      }
    }
  }

  return null;
};

export const addLabelForGraphic = (
  mapView: MapView,
  graphic: Graphic | null,
  label: string = 'Untitled',
  labeLayerId: string = LABEL_LAYER_ID,
) => {
  if (!graphic?.geometry) return;

  const titleLabel = label.length > MAX_LABEL_CHAR_COUNT
    ? `${label.substring(0, MAX_LABEL_CHAR_COUNT - 4)} ...` // adds elypsis to long titles
    : label;

  let x: number;
  let y: number;
  let geometry;

  switch (graphic?.geometry?.type) {
    case 'point':
      geometry = graphic.geometry;
      x = geometry.longitude;
      y = geometry.latitude;
      break;
    case 'polygon':
    case 'polyline':
      geometry = graphic.geometry;
      x = geometry.extent.center.x;
      y = geometry.extent.center.y;
      break;
    default:
      console.error('Unhandled geometry type:', graphic.geometry.type);
      return;
  }

  if (x === undefined || y === undefined) return;

  const labelPoint = new Point({ x, y });
  const textSymbol = new TextSymbol({
    // adding unicode non-breaking space character to add required padding to label
    text: `\u00A0\u00A0\u00A0${titleLabel}\u00A0\u00A0\u00A0\u00A0\u00A0`,
    color: 'black',
    haloColor: 'white',
    haloSize: 1,
    font: {
      size: 10,
      family: 'Roboto',
      weight: 'bold',
    },
    borderLineSize: 1,
    backgroundColor: [255, 255, 255, 1],
    lineWidth: 200,
    lineHeight: 20,
    verticalAlignment: 'middle',
    horizontalAlignment: 'center',
    yoffset: LABEL_BOX_Y_OFFSET,
  });

  const pointerSymbol = new SimpleMarkerSymbol({
    color: 'white',
    style: 'triangle',
    size: '10px',
    angle: 180,
    outline: {
      width: 0,
    },
    yoffset: POINTER_SYMBOL_Y_OFFSET,
  });

  const labelGraphic = new Graphic({
    geometry: labelPoint,
    symbol: textSymbol,
    attributes: { type: 'text_label' },
  });

  const pointerGraphic = new Graphic({
    geometry: labelPoint,
    symbol: pointerSymbol,
    attributes: { type: 'pointer' },
  });

  let labelGraphicsLayer = mapView.map.findLayerById(labeLayerId) as GraphicsLayer;
  if (!labelGraphicsLayer) {
    labelGraphicsLayer = new GraphicsLayer({ id: labeLayerId });
    mapView.map.add(labelGraphicsLayer);
  }
  labelGraphicsLayer.addMany([labelGraphic, pointerGraphic]);
};

export const removeTextLabelGraphics = (mapView: MapView, labelLayerId: string = LABEL_LAYER_ID) => {
  const labelGraphicsLayer = mapView.map.findLayerById(labelLayerId);

  if (labelGraphicsLayer) {
    (labelGraphicsLayer as GraphicsLayer).removeAll();
  }
};

export const buildLabelFromGraphicWithShape = (graphic: Graphic | null): string | undefined => {
  if (!graphic) return undefined;
  const { item } = graphic?.attributes?.shape ?? { item: { displayValue: '' } };
  const { displayValue } = item;

  return TextUtils.isEmpty(displayValue) ? 'Untitled' : displayValue;
};
